import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Badge,
  Flex,
  Skeleton,
  Tooltip
} from '@chakra-ui/react';
import Link from 'next/link';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCalendarPlus
} from '@fortawesome/pro-regular-svg-icons';

import { EventType, IMorressierEvent } from 'typings/event';
import { getFormattedDateRange } from 'utils/utils';

const ITEMS_PER_PAGE = 5; // Number of events to show per page in Manage Events Table

interface Props {
  hostingEvents: any[];
  attendingEvents: any[];
  reviewingEvents: any[];
  isLoading: boolean;
}

interface ManageEventsHandle {
  scrollToManageTasks: () => void;
}

const ManageEvents: React.FC<Props & React.RefAttributes<ManageEventsHandle>> =
  forwardRef((props, ref) => {
    const manageTasksRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollToManageTasks() {
        if (manageTasksRef.current) {
          manageTasksRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }));

    const { hostingEvents, reviewingEvents, attendingEvents, isLoading } =
      props;
    // state
    const [currentPage, setCurrentPage] = useState(1);

    const allUserEvents = [
      ...hostingEvents,
      ...reviewingEvents,
      ...attendingEvents
    ]
      .filter(
        event =>
          event.roles?.includes('organizer') ||
          event.roles?.includes('section_editor')
      )
      .sort((a, b) => {
        const event1StartDate = new Date(a.start_date);
        const event2StartDate = new Date(b.start_date);
        return event1StartDate.getTime() > event2StartDate.getTime() ? -1 : 1;
      });

    const renderSkeleton = () => {
      return isLoading
        ? Array.from({ length: ITEMS_PER_PAGE }).map((_value, index) => (
            <Tr key={index}>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
            </Tr>
          ))
        : null;
    };

    // Function to paginate the events array
    const getPaginatedEvents = (
      events: (IMorressierEvent & EventType)[],
      currentPage: number
    ) => {
      // const totalEvents = events.length;
      // const totalPages = Math.ceil(totalEvents / ITEMS_PER_PAGE);
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      return events.slice(startIndex, endIndex);
    };

    const totalPages = Math.ceil(allUserEvents.length / ITEMS_PER_PAGE);

    const isLastPage = currentPage === totalPages;
    const isFirstPage = currentPage === 1;

    const nextPage = () => {
      if (isLastPage) {
        return;
      }
      setCurrentPage(currentPage + 1);
    };

    const prevPage = () => {
      if (isFirstPage) {
        return;
      }
      setCurrentPage(currentPage - 1);
    };

    const ManageTableThText = ({ children }: { children: string }) => (
      <Text
        textStyle="headingItem"
        textTransform="capitalize"
        textColor="neutralPrimaryContent"
        fontWeight={500}
        letterSpacing={0}
      >
        {children}
      </Text>
    );

    const paginatedEvents = getPaginatedEvents(allUserEvents, currentPage);

    const renderModule = (event: IMorressierEvent & EventType) => {
      const modules = [];

      if (
        event.abstract_management_enabled ||
        event.poster_presentation_management_enabled
      ) {
        modules.push('Abstracts');
      }

      if (event.proceedings_enabled) {
        modules.push('Proceedings');
      }

      if (modules.length === 0) {
        return null;
      }

      return (
        <Flex gap="2">
          {modules.map((module, index) => (
            <Badge key={module + index} variant="neutral" size="context">
              {module}
            </Badge>
          ))}
        </Flex>
      );
    };

    const handleManageEvent = (eventId: string) => {
      window.open(`/conference/event/${eventId}`, '_blank');
    };

    const handleCreateEvent = () => {
      window.open('/conference/create-event', '_blank');
    };

    return (
      <div ref={manageTasksRef}>
        <Flex justifyContent="space-between" marginBottom="8">
          <Text textStyle="headingSectionBrand">Manage Events</Text>
          <ButtonText
            iconLeft={<FontAwesomeIcon icon={faCalendarPlus} />}
            onClick={() => handleCreateEvent()}
          >
            Create Event
          </ButtonText>
        </Flex>
        <TableContainer width={{ base: 'full' }} mx="auto" marginBottom="8">
          <Table variant="simple">
            <Thead backgroundColor="surfaceTertiaryFill">
              <Tr>
                <Th width="23%">
                  <ManageTableThText>Event</ManageTableThText>
                </Th>
                <Th width="23%">
                  <ManageTableThText>Acronym</ManageTableThText>
                </Th>
                <Th width="23%">
                  <ManageTableThText>Event Date</ManageTableThText>
                </Th>
                <Th width="23%">
                  <ManageTableThText>Module</ManageTableThText>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {renderSkeleton()}

              {paginatedEvents.map(event => (
                <Tr
                  key={event._id}
                  borderBottom="1px"
                  borderColor="neutralPrimaryBorder"
                  backgroundColor="surfacePrimaryFill"
                >
                  <Td
                    maxWidth="318px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    <Tooltip label={event.name} placement="top" hasArrow>
                      {event.name || ''}
                    </Tooltip>
                  </Td>
                  <Td>{event.short_name}</Td>
                  <Td>
                    {getFormattedDateRange(
                      new Date(event.start_date),
                      new Date(event.end_date)
                    )}
                  </Td>
                  <Td>{renderModule(event)}</Td>
                  <Td textAlign="end">
                    <ButtonText onClick={() => handleManageEvent(event.id)}>
                      Manage
                    </ButtonText>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Flex justifyContent="end" marginBottom="8" gap="4">
            <ButtonText
              iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={prevPage}
              disabled={isFirstPage}
            >
              Prev
            </ButtonText>
            <ButtonText
              iconRight={<FontAwesomeIcon icon={faArrowRight} />}
              onClick={nextPage}
              disabled={isLastPage}
            >
              Next
            </ButtonText>
          </Flex>
        )}
      </div>
    );
  });

export default ManageEvents;
